<template>
  <v-dialog :value="dialogProp.show" persistent max-width="600">
    <v-card class="elevation-12">
      <v-toolbar dark color="primary">
        <v-toolbar-title>
          {{ $t('Добавление доверенности') }}
        </v-toolbar-title>
      </v-toolbar>
      <v-row v-if="!!generalError || isLoading" class="my-0 py-0 mx-1">
        <v-col cols="12">
          <v-progress-linear :active="isLoading" color="loader" indeterminate />
        </v-col>
        <v-col class="mx-1">
          <general-error v-if="!!generalError" />
        </v-col>
      </v-row>
      <v-card-text class="pt-3">
        <v-row class="subheading font-weight-bold text-left mt-2">
          <v-col cols="1" class="red--text" align-self="center">
            *
          </v-col>
          <v-col cols="9" class="text-left" align-self="center">
            <link-with-tip
                v-if="templatePath"
                :href="templatePath"
                :tip="$t('Скачать шаблон документа')"
                :text="documentDescription" />
            <span v-else>
              {{ documentDescription }}
            </span>
          </v-col>
          <v-col cols="2" class="text-right" align-self="center">
            <typed-file-upload
                :document-type="documentType"
                :document-group="dialogProp.documentGroup"
                :upload-method="uploadSingleIdentityFile" />
          </v-col>
        </v-row>
        <v-divider />
        <v-row>
          <v-col>
            <ValidationObserver ref="proxy">
              <validatable-text-input
                  v-model="file.fileName"
                  rules="required"
                  :label="documentDescription"
                  prepend-icon="attach_file"
                  readonly />
              <v-divider />
              <user-proxy-info
                  v-if="isUserProxy"
                  ref="proxyInfo" />
            </ValidationObserver>
          </v-col>
        </v-row>
        <v-divider v-if="file.fileId" />
        <v-row v-if="file.fileId">
          <v-col cols="12" class="text-center">
            <btn-icon-tip
                id="getFile"
                icon="get_app"
                :tip="$t('Скачать документ')"
                @click="getIdentityFile(file.fileId)" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
            id="cancelBtn"
            v-test="{ btn: 'cancel' }"
            color="primary"
            class="mr-2"
            @click.stop="cancel">
          {{ $t('Отмена') }}
        </v-btn>
        <v-btn
            id="continueBtn"
            ref="continueBtn"
            v-test="{ btn: 'continue' }"
            color="primary"
            :disabled="isLoading"
            @click.stop="continueOperation">
          {{ $t('Продолжить') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import Vue from 'vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import UserProxyInfo from '@/components/reused/proxies/UserProxyInfo.vue'
  import TypedFileUpload from '@/components/reused/ui/TypedFileUpload.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import LinkWithTip from '@/components/reused/ui/LinkWithTip.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import getFileMixin from '@/utils/mixins/scoped/getFileMixin.js'
  import { DOCUMENT_TYPE_ENUM } from '@/constants/enums'
  import { DOCUMENT_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { IDENTITY_FILES } from '@/constants/store.js'

  export default {
    components: {
      ValidatableTextInput,
      UserProxyInfo,
      TypedFileUpload,
      BtnIconTip,
      LinkWithTip,
      GeneralError
    },
    mixins: [
      getFileMixin('isLoading')
    ],
    props: {
      dialogProp: {
        type: Object,
        default: () => {
          return {
            show: false,
            proxyType: null,
            documentGroup: null,
            callback: null
          }
        }
      }
    },
    data () {
      return {
        isLoading: false,
        documentType: this.dialogProp.proxyType,
        DOCUMENT_TYPES
      }
    },
    computed: {
      ...mapState(IDENTITY_FILES.NAMESPACE, {
        file: state => (state[IDENTITY_FILES.STATE.CURRENT_FILE] || {})
      }),
      templatePath () {
        const template = this.getDocumentTemplateName(this.documentType, this.systemLocale)
        const templatePath = template
          ? this.getSystemFileUrl(template)
          : null
        return templatePath
      },
      documentDescription () {
        return this.$t(DOCUMENT_TYPE_ENUM[this.documentType])
      },
      isUserProxy () {
        return this.documentType === DOCUMENT_TYPES.USER_PROXY
      }
    },
    created () {
      this.clearStore()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        uploadSingleIdentityFileAction: IDENTITY_FILES.ACTIONS.UPLOAD_SINGLE_IDENTITY_FILE,
        clearSingleIdentityFileAction: IDENTITY_FILES.ACTIONS.CLEAR_SINGLE_IDENTITY_FILE
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearSingleIdentityFileAction()
          ])
      },
      continueOperation: async function () {
        try {
          const result = await this.$refs.proxy.validate()
          if (!result) {
            throw new Vue.errorsTypes.ValidationError()
          }
          if (this.dialogProp.callback) {
            const proxy = await this.$refs.proxyInfo.getData()

            this.isLoading = true
            await this.dialogProp.callback({
              ...proxy,
              fileId: this.file.fileId
            })
            this.isLoading = false
            this.cancel()
          }
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
        }
      },
      uploadSingleIdentityFile: async function (form, documentType) {
        // try catch не нужен (если ошибка, обработаем в месте вызова метода)
        await this.uploadSingleIdentityFileAction({
          form,
          documentType
        })
      },
      cancel: function () {
        this.$emit('close')
      }
    }
  }
</script>
